/**
 * Utility functions for parsing and handling error messages
 */

/**
 * Parses error message string and extracts individual errors
 * @param errorMessage - The complete error message string
 * @returns Array of individual error messages
 */
export const parseErrorMessages = (errorMessage: string): string[] => {
  if (!errorMessage || typeof errorMessage !== 'string') {
    return [errorMessage || 'Unknown error occurred'];
  }

  // First, try to split by file blocks (each starts with filename and colon)
  const fileBlocks = errorMessage.split(/(?<=\n)\s*(?=[^\s]+\.csv:)/).filter(Boolean);
  
  if (fileBlocks.length > 1) {
    // Multiple file blocks detected - parse each block separately
    let allErrors: string[] = [];
    fileBlocks.forEach((block, idx) => {
      const maErrorDelimiter = "MA System Errors";
      let fieldErrorString = block;
      let maErrorString = '';
      if (block.includes(maErrorDelimiter)) {
        const parts = block.split(maErrorDelimiter);
        fieldErrorString = parts[0] || '';
        maErrorString = parts[1] || '';
      }
      const fieldErrors = parseErrorBlock(fieldErrorString);
      const maErrors = parseErrorBlock(maErrorString);
      allErrors.push(...fieldErrors);
      if (maErrors.length > 0) {
        allErrors.push('', '🔴 MA System Errors:', ...maErrors);
      }
      // Add a blank line after each file block except the last one
      if (idx < fileBlocks.length - 1) {
        allErrors.push('');
      }
    });
    return allErrors.length > 0 ? allErrors : [errorMessage];
  } else {
    // Single block or no file blocks - parse as general error message
    const maErrorDelimiter = "MA System Errors";
    let fieldErrorString = errorMessage;
    let maErrorString = '';
    if (errorMessage.includes(maErrorDelimiter)) {
      const parts = errorMessage.split(maErrorDelimiter);
      fieldErrorString = parts[0] || '';
      maErrorString = parts[1] || '';
    }
    const fieldErrors = parseErrorBlock(fieldErrorString);
    const maErrors = parseErrorBlock(maErrorString);
    let allErrors = [...fieldErrors];
    if (maErrors.length > 0) {
      allErrors.push('', '🔴 MA System Errors:', ...maErrors);
    }
    return allErrors.length > 0 ? allErrors : [errorMessage];
  }
};

/**
 * Helper function to parse a single error block into individual errors
 * @param errorBlock - A block of error text
 * @returns Array of individual error messages
 */
const parseErrorBlock = (errorBlock: string): string[] => {
  if (!errorBlock || typeof errorBlock !== 'string') {
    return [];
  }

  const trimmedBlock = errorBlock.trim();
  if (!trimmedBlock) return [];

  let errors: string[] = [];

  // Split by line breaks first
  if (trimmedBlock.includes('\n')) {
    const lines = trimmedBlock.split('\n').map(line => line.trim()).filter(Boolean);
    
    // Look for lines that contain error patterns
    const errorLines = lines.filter(line => 
      /^Error.*line/i.test(line) || 
      /error.*line/i.test(line) || 
      /^.*\.(csv|txt):/i.test(line) ||
      line.length > 10 // Fallback for substantial error messages
    );
    
    if (errorLines.length > 0) {
      errors = errorLines;
    } else {
      // If no specific error lines found, treat each line as a potential error
      errors = lines.filter(line => line.length > 0);
    }
  }
  // Split by "Error" keyword if present
  else if (trimmedBlock.toLowerCase().includes('error') && /\s+Error\s+/.test(trimmedBlock)) {
    errors = trimmedBlock.split(/\s+Error\s+/).map(error => error.trim()).filter(Boolean);
    // Re-add "Error" prefix if it was split
    errors = errors.map((error, index) => index > 0 ? `Error ${error}` : error);
  }
  // Single error message
  else {
    errors = [trimmedBlock];
  }

  return errors.filter(e => e.length > 0);
};

/**
 * Formats error message for display in toast
 * @param errors - Array of error messages
 * @param maxDisplay - Maximum number of errors to display in toast (default: 5)
 * @returns Object with formatted message and whether to show "View All" button
 */
export const formatErrorsForToast = (errors: string[], maxDisplay: number = 5) => {
  if (errors.length <= maxDisplay) {
    return {
      message: errors.join(', '),
      showViewAll: false,
      fullErrors: errors
    };
  }

  const displayedErrors = errors.slice(0, maxDisplay);
  const remainingCount = errors.length - maxDisplay;
  
  return {
    message: `${displayedErrors.join(', ')} ... and ${remainingCount} more error${remainingCount > 1 ? 's' : ''}`,
    showViewAll: true,
    fullErrors: errors
  };
};

/**
 * Checks if an error message contains multiple errors (more than specified threshold)
 * @param errorMessage - The error message to check
 * @param threshold - The threshold for considering it as "multiple errors" (default: 5)
 * @returns Boolean indicating if there are multiple errors
 */
export const hasMultipleErrors = (errorMessage: string, threshold: number = 5): boolean => {
  const errors = parseErrorMessages(errorMessage);
  return errors.length > threshold;
};
